import { useMemo, useRef } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Image as ImageIcon, Info } from 'lucide-react';
import { PriceUnit, ProductCondition, ProductStatus, type Product, type ProductImageInput } from '@ncc/shared';
import { api } from '../lib/api';
import { ImageManager } from './ImageManager';
import { PriceUnitSelect } from './PriceUnitSelect';

// Empty <select>/number inputs submit '' — turn that into null BEFORE coercion.
// Without this, z.coerce.number()('') === 0, which fails the API's
// `.positive().nullable()` id rules (→ 400) and stores $0 for blank prices.
const emptyToNull = (v: unknown) => (v === '' || v == null ? null : v);
const nullableId = z.preprocess(emptyToNull, z.coerce.number().int().positive().nullable());
const nullableMoney = z.preprocess(emptyToNull, z.coerce.number().nonnegative().nullable());
const nullableUnit = z.preprocess(emptyToNull, PriceUnit.nullable());

const FormSchema = z.object({
  name: z.string().min(1).max(200),
  sku: z.string().max(100).optional(),
  status: ProductStatus,
  condition: ProductCondition,
  brand_id: nullableId,
  category_id: nullableId,
  regular_price: nullableMoney,
  sale_price: nullableMoney,
  price_unit: nullableUnit,
  description: z.string().optional(),
});
export type ProductFormValues = z.infer<typeof FormSchema>;

type Props = {
  initial?: Product;
  submitLabel: string;
  onSubmit: (values: ProductFormValues, images: ProductImageInput[]) => Promise<void>;
  initialImages?: ProductImageInput[];
};

export function ProductForm({ initial, submitLabel, onSubmit, initialImages }: Props) {
  const form = useForm<ProductFormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: useMemo(() => toDefaults(initial), [initial]),
  });
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form;

  const imagesRef = useRef<ProductImageInput[]>(
    initialImages ??
      (initial?.images.map((i) => ({ url: i.url, image_id: i.image_id, position: i.position })) ??
        []),
  );

  const brandsQ = useQuery({ queryKey: ['brands'], queryFn: api.listBrands });
  const categoriesQ = useQuery({
    queryKey: ['product-categories'],
    queryFn: api.listProductCategories,
  });

  const submit: SubmitHandler<ProductFormValues> = async (values) => {
    await onSubmit(values, imagesRef.current);
  };

  const errorOf = (key: keyof ProductFormValues) =>
    errors[key] ? <div className="error-text">{errors[key]?.message as string}</div> : null;

  return (
    <form onSubmit={handleSubmit(submit)} noValidate>
      <h3 className="section-title">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <ImageIcon size={14} /> Photos
        </span>
      </h3>
      <ImageManager
        initial={imagesRef.current as never}
        onChange={(next) => {
          imagesRef.current = next as ProductImageInput[];
        }}
      />

      <h3 className="section-title">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Info size={14} /> Product details
        </span>
      </h3>

      <div className="grid-form">
        <div className="field-span-12">
          <label>Name *</label>
          <input placeholder="e.g. Used 11R22.5 drive tire (pair)" {...register('name')} />
          {errorOf('name')}
        </div>

        <div className="field-span-4">
          <label>Condition</label>
          <select {...register('condition')}>
            <option value="used">Used</option>
            <option value="new">New</option>
          </select>
        </div>
        <div className="field-span-4">
          <label>SKU</label>
          <input placeholder="Internal SKU (optional)" {...register('sku')} />
          {errorOf('sku')}
        </div>
        <div className="field-span-4">
          <label>Status</label>
          <select {...register('status')}>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="hidden">Hidden</option>
            <option value="out_of_stock">Out of stock</option>
          </select>
        </div>

        <div className="field-span-4">
          <label>Brand</label>
          <select {...register('brand_id')}>
            <option value="">—</option>
            {brandsQ.data?.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field-span-4">
          <label>Category</label>
          <select {...register('category_id')}>
            <option value="">—</option>
            {categoriesQ.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field-span-2">
          <label>Regular price</label>
          <input type="number" step="0.01" placeholder="$" {...register('regular_price')} />
        </div>
        <div className="field-span-2">
          <label>Sale price</label>
          <input type="number" step="0.01" placeholder="$" {...register('sale_price')} />
        </div>
        <div className="field-span-2">
          <label>Price is per</label>
          <PriceUnitSelect register={register('price_unit')} />
        </div>

        <div className="field-span-12">
          <label>Description</label>
          <textarea
            rows={6}
            placeholder="Optional sales description shown on the public listing."
            {...register('description')}
          />
          {errorOf('description')}
        </div>
      </div>

      <div className="toolbar form-actions">
        <button className="primary" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

function toDefaults(initial?: Product): Partial<ProductFormValues> {
  if (!initial) {
    return { status: 'draft', condition: 'used' };
  }
  return {
    name: initial.name,
    sku: initial.sku ?? '',
    status: initial.status,
    condition: initial.condition,
    brand_id: initial.brand_id ?? null,
    category_id: initial.category_id ?? null,
    regular_price: initial.regular_price ?? undefined,
    sale_price: initial.sale_price ?? undefined,
    price_unit: initial.price_unit ?? null,
    description: initial.description ?? '',
  };
}
